import React, { useEffect, useRef, useState } from 'react';

// cw: Registration List — reduced-click action menu. Shows next to a person (photolist
// card) or near their email (Members row). Two actions, labels are the group's custom
// connectMeet/testimonial labels (never hard-coded). `mode` only changes the wording:
//  - 'event'  → "Request a {CM}" / "Give a {TY}" (immediate, tied to the current event)
//  - 'future' → "...with this person in the future" (deferred queue)
type Props = {
    connectMeetLabel: string;
    testimonialLabel: string;
    mode?: 'event' | 'future';
    onRequestCm: () => void;
    onRequestTy: () => void;
    disabled?: boolean;
    busy?: boolean;
    align?: 'left' | 'right';
};

const QuickActionMenu: React.FC<Props> = ({
    connectMeetLabel,
    testimonialLabel,
    mode = 'event',
    onRequestCm,
    onRequestTy,
    disabled = false,
    busy = false,
    align = 'right',
}) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const onDocClick = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, []);

    const cmText = mode === 'future'
        ? `Request ${connectMeetLabel}`
        : `Request a ${connectMeetLabel}`;
    const tyText = mode === 'future'
        ? `Create ${testimonialLabel}`
        : `Give a ${testimonialLabel}`;

    const run = (fn: () => void) => {
        setOpen(false);
        fn();
    };

    return (
        <div className="relative inline-block" ref={ref}>
            <button
                type="button"
                aria-label="Actions"
                disabled={disabled || busy}
                onClick={() => setOpen(o => !o)}
                className="w-9 h-9 rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 flex items-center justify-center text-xl leading-none"
            >
                {busy ? '…' : '⋯'}
            </button>
            {open && (
                <div
                    className={`absolute z-30 mt-1 w-72 rounded-lg border border-gray-200 bg-white shadow-lg p-2 flex flex-col gap-2 ${align === 'right' ? 'right-0' : 'left-0'}`}
                >
                    <button
                        type="button"
                        onClick={() => run(onRequestCm)}
                        className="w-full flex items-center gap-2 rounded-md border border-orange-200 bg-orange-50 px-3 py-2 text-sm font-medium text-orange-700 hover:bg-orange-100 hover:border-orange-300"
                    >
                        <span className="text-left">{cmText}</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => run(onRequestTy)}
                        className="w-full flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm font-medium text-green-700 hover:bg-green-100 hover:border-green-300"
                    >
                        <span className="text-left">{tyText}</span>
                    </button>
                </div>
            )}
        </div>
    );
};

export default QuickActionMenu;
